import styled from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import Menu from "./Menu";
import Button from "../Button";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
} from "../../../utils/miscellaneous";
import { localStorageConstants } from "../../../utils/constants";
import { TInvoice } from "../../../redux/reducers/globalState";

import apiService from "../../../services/api";
import useDetectOutsideClick from "../../../hooks/useDetectOutsideClick";
import axios, { CancelTokenSource } from "axios";
import { fetchInvoices } from "../../../redux/actions";
import { useNavigationManager } from "../../../hooks/useNavigationManager";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";

const Container = styled.div`
  position: relative;
`;

export type TContent =
  | "editStatus"
  | "markAsFinished"
  | "markAsPending"
  | "downloadFile"
  | "deletePaymentSlip"
  | "openPayableModal"
  | "openGoodsModal"
  | "importFile"
  | "linkPaymentSlip";

export type TCommonProps = {
  content: TContent[];
  onMenuSelect?: (selected: string) => void;
  onClick?: (select: TContent) => void;
  isDisabled?: boolean;
  scrollableRef?: React.MutableRefObject<HTMLTableSectionElement | null>;
};

export type TDropdownOnlyIconProps = TCommonProps;

const DropdownOnlyIcon: React.FC<TDropdownOnlyIconProps> = ({
  content,
  onMenuSelect,
  onClick,
  isDisabled,
}) => {
  const [selectedData, setSelectedData] = useState("");
  const [shouldShow, setShouldShow] = useState<"show" | "hide">("hide");

  const [shouldShowNextMenu, setshouldShowNextMenu] = useState(false);

  const dispatch = useTypedDispatch();

  const componentRef = useRef<HTMLDivElement | null>(null);

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const fetchInvoicesCancelToken = useRef<CancelTokenSource>(
    axios.CancelToken.source()
  );

  const { goTo } = useNavigationManager();

  useDetectOutsideClick({
    isClicked: shouldShow === "show",
    componentRef,
    onOutside() {
      if (selectedData.length === 0) {
        setShouldShow("hide");
        setshouldShowNextMenu(false);
      }
    },
  });

  const handleRenderDropdown = useCallback(() => {
    const handleOnClickButton = () => {
      setShouldShow((prev) => {
        if (prev === "show") {
          setshouldShowNextMenu(false);
        }
        return prev === "hide" ? "show" : "hide";
      });
    };

    const handleOnBlur = (): "hide" | "show" => {
      return "hide";
    };

    cancelTokenSource.current = axios.CancelToken.source();

    const handleMarkAsFinished = () => {
      const localSelectInvoice = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_INVOICE
      ) as TInvoice;

      if (localSelectInvoice) {
        apiService
          .updateInvoiceMarkAsFinished(
            localSelectInvoice.uuid,
            (cancelTokenSource.current as CancelTokenSource).token
          )
          .then(() => {
            const querystring = getQuerystringParams();
            goTo(".", querystring);

            dispatch(
              fetchInvoices({
                cancelToken: fetchInvoicesCancelToken.current.token,
              })
            );
          });
      }
    };

    const handleMarkAsPending = () => {
      const localSelectInvoice = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_INVOICE
      ) as TInvoice;

      if (localSelectInvoice) {
        apiService
          .updateInvoiceMarkAsPending(
            localSelectInvoice.uuid,
            (cancelTokenSource.current as CancelTokenSource).token
          )
          .then(() => {
            const querystring = getQuerystringParams();
            goTo(".", querystring);

            if (fetchInvoicesCancelToken.current) {
              dispatch(
                fetchInvoices({
                  cancelToken: fetchInvoicesCancelToken.current.token,
                })
              );
            }
          });
      }
    };

    const handleOnClickMenu = (index: string, selected: TContent[]) => {
      setSelectedData(content[parseInt(index)]);

      selected.map((option) => {
        switch (option) {
          case "editStatus":
            if (onClick) onClick("editStatus");
            break;
          case "markAsFinished":
            if (onClick) onClick("markAsFinished");
            handleMarkAsFinished();
            break;
          case "markAsPending":
            if (onClick) onClick("markAsPending");
            handleMarkAsPending();
            break;
          case "downloadFile":
            if (onClick) onClick("downloadFile");
            break;
          case "deletePaymentSlip":
            if (onClick) onClick("deletePaymentSlip");
            break;
          case "openGoodsModal":
            if (onClick) onClick("openGoodsModal");
            break;
          case "openPayableModal":
            if (onClick) onClick("openPayableModal");
            break;
          case "importFile":
            if (onClick) onClick("importFile");
            break;
          case "linkPaymentSlip":
            if (onClick) onClick("linkPaymentSlip");
            break;
        }
      });

      if (onMenuSelect) onMenuSelect(`${content[parseInt(index)]}`);

      setShouldShow("hide");
    };

    return (
      <Container ref={componentRef}>
        <Button
          onClick={handleOnClickButton}
          onBlur={handleOnBlur}
          variant="secondary"
          icon="onlyIcon"
          iconType="ellipsisV"
          disabled={content.length === 0 || isDisabled}
        />

        {content.length > 0 && (
          <Menu
            shouldShow={shouldShow}
            content={content}
            list="default"
            onClick={handleOnClickMenu}
            setShouldShow={setShouldShow}
            setshouldShowNextMenu={setshouldShowNextMenu}
            shouldShowNextMenu={shouldShowNextMenu}
          />
        )}
      </Container>
    );
  }, [shouldShow, selectedData]);

  useEffect(() => {
    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, []);

  return handleRenderDropdown();
};

export default DropdownOnlyIcon;
